// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Col, Form, Input, Row, Select, Steps} from "antd";
import * as AuthBackend from "../../services/AuthBackend";
import * as ApplicationBackend from "../../services/ApplicationBackend";
import * as Util from "../../utils/Util";
import * as Setting from "../../conf/Setting";
import i18next from "i18next";
import {CountDownInput} from "../../components/common/CountDownInput";
import * as UserBackend from "../../services/UserBackend";
import {CheckCircleOutlined, KeyOutlined, LockOutlined, SolutionOutlined, UserOutlined} from "@ant-design/icons";
import CustomGithubCorner from "../../components/common/CustomGithubCorner";
import {JSEncrypt} from "jsencrypt";
import SelectLanguageBox from "../../components/common/SelectLanguageBox";
import * as PasswordPolicyBackend from "../../services/PasswordPolicyBackend";

const {Step} = Steps;
const {Option} = Select;

class ForgetPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      account: props.account,
      applicationId:
          props.applicationId !== undefined
            ? props.applicationId
            : props.match === undefined
              ? null
              : props.match.params.applicationId,
      application: null,
      msg: null,
      userId: "",
      username: "",
      name: "",
      email: "",
      isFixed: false,
      fixedContent: "",
      token: "",
      phone: "",
      emailCode: "",
      phoneCode: "",
      verifyType: null, // "email" or "phone"
      current: 0,
      pwdPolicy: null,
    };

    this.onFinish = this.onFinish.bind(this);
  }

  UNSAFE_componentWillMount() {
    if (this.state.applicationId !== undefined) {
      this.getApplication();
    } else {
      Util.showMessage(
        "error",
        i18next.t("forget:Unknown forget type: ") + this.state.type
      );
    }
  }

  getApplication() {
    if (this.state.applicationId === null) {
      return;
    }

    ApplicationBackend.getApplication(Setting.Admin, this.state.applicationId).then(
      (application) => {
        this.setState({
          application: application,
        });
      }
    );
  }

  getApplicationObj() {
    if (this.props.application !== undefined) {
      return this.props.application;
    } else {
      return this.state.application;
    }
  }

  onFormFinish(name, info, forms) {
    switch (name) {
    case "step1":
      const username = forms.step1.getFieldValue("username");
      AuthBackend.getEmailAndPhone({
        application: forms.step1.getFieldValue("application"),
        organization: forms.step1.getFieldValue("organization"),
        username: username,
      }).then((res) => {
        if (res.status === "ok") {
          const phone = res.data.phone;
          const email = res.data.email;
          // this.setState({phone: phone, email: email, username: res.data.name, name: res.data.name});
          this.setState({phone: phone, email: email, name: res.data.name});

          let fixedContent = "";
          let isFixed = false;
          switch (res.data2) {
          case "email":
            this.setState({isFixed: true, fixedContent: email, verifyType: "email"});
            isFixed = true;
            fixedContent = email;
            break;
          case "phone":
            this.setState({isFixed: true, fixedContent: phone, verifyType: "phone"});
            isFixed = true;
            fixedContent = phone;
            break;
          case "username":
            this.setState({verifyType: ""});
            break;
          default:
            break;
          }
          if (isFixed) {
            forms.step2.setFieldsValue({email: fixedContent});
            this.setState({username: fixedContent});
          }
          this.setState({current: 1});
        } else {
          Setting.showResponseMessage(res.msg);
        }
      });
      break;
    case "step2":
      const oAuthParams = Util.getOAuthGetParameters();
      let userName = this.state.username;
      if (this.state.verifyType === "email") {
        this.setState({username: this.state.email});
        userName = this.state.email;
      } else if (this.state.verifyType === "phone") {
        this.setState({username: this.state.phone});
        userName = this.state.phone;
      }

      AuthBackend.login({
        application: forms.step2.getFieldValue("application"),
        organization: forms.step2.getFieldValue("organization"),
        username: userName,
        name: this.state.name,
        code: forms.step2.getFieldValue("emailCode"),
        phonePrefix: this.state.application?.organizationObj.phonePrefix,
        type: "login",
      }, oAuthParams).then(res => {
        if (res.status === "ok") {
          this.setState({current: 2, userId: res.data, username: res.data.split("/")[1]});

          const applicationObj = this.getApplicationObj();
          PasswordPolicyBackend.getConfig(Setting.Admin, applicationObj.organizationObj.pwdConfig)
            .then(resConfig => {
              if (resConfig.msg === undefined) {
                this.setState({
                  pwdPolicy: resConfig,
                });
              } else {
                Setting.showResponseMessage(resConfig.msg);
              }
            });
        } else {
          Setting.showResponseMessage(res.msg);
        }
      });
      break;
    default:
      break;
    }
  }

  onFinish(values) {
    AuthBackend.getPublicKey().then(resPubKey => {
      if (resPubKey.status === "ok") {
        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(resPubKey.msg);
        const newPassword = encrypt.encrypt(values?.newPassword);
        values.username = this.state.username;
        values.userOwner = this.state.application?.organizationObj.uid;

        const applicationObj = this.getApplicationObj();
        PasswordPolicyBackend.checkPassword(applicationObj.organizationObj.owner, applicationObj.organizationObj.uid, applicationObj.organizationObj.uid, values.username, newPassword)
          .then(resCheck => {
            if (resCheck.status === "ok") {
              UserBackend.setPassword(values.userOwner, values.username, "", newPassword).then(res => {
                if (res.status === "ok") {
                  AuthBackend.logout().then(resLogout => {
                    if (resLogout.status === "ok") {
                      const params = new URLSearchParams(window.location.search);
                      const loginUrl = Util.decodeUrl(params.get("login_url"));
                      if (loginUrl) {
                        Setting.goToLink(loginUrl);
                      } else {
                        Setting.goToLink("/");
                      }
                    } else {
                      Setting.showResponseMessage(resLogout.msg);
                    }
                  });
                } else {
                  Setting.showResponseMessage(res.msg);
                }
              });
            } else {
              Setting.showResponseMessage(resCheck.msg);
            }
          });
      } else {
        Setting.showResponseMessage(resPubKey.msg);
      }
    });
  }

  onFinishFailed(values, errorFields) {}

  renderOptions() {
    const options = [];

    if (this.state.phone !== "") {
      options.push(
        <Option key={"phone"} value={"phone"}>
          &nbsp;&nbsp;{this.state.phone}
        </Option>
      );
    }

    if (this.state.email !== "") {
      options.push(
        <Option key={"email"} value={"email"}>
          &nbsp;&nbsp;{this.state.email}
        </Option>
      );
    }

    return options;
  }

  renderForm(application) {
    return (
      <Form.Provider onFormFinish={(name, {info, forms}) => {
        this.onFormFinish(name, info, forms);
      }}>
        {/* STEP 1: input username -> get email & phone */}
        <Form
          hidden={this.state.current !== 0}
          ref={this.form}
          name="step1"
          // eslint-disable-next-line no-console
          onFinishFailed={(errorInfo) => console.log(errorInfo)}
          initialValues={{
            application: application.uid,
            organization: application.organization,
          }}
          style={Setting.isMobile() ? {} : {width: "300px"}}
          size="large"
        >
          <Form.Item
            style={{height: 0, visibility: "hidden"}}
            name="application"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "forget:Please input your application!"
                ),
              },
            ]}
          />
          <Form.Item
            style={{height: 0, visibility: "hidden"}}
            name="organization"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "forget:Please input your organization!"
                ),
              },
            ]}
          />
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "forget:Please input your username!"
                ),
                whitespace: true,
              },
              {
                validator: (_, value) => {
                  if (value === undefined) {
                    return Promise.resolve();
                  }
                  if (value.length > 50) {
                    return Promise.reject(
                      i18next.t("general:Limited number of characters is {number}").replace("{number}", 50)
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              onChange={(e) => {
                this.setState({
                  username: e.target.value,
                });
              }}
              prefix={<UserOutlined />}
              placeholder={i18next.t("login:username")}
            />
          </Form.Item>
          <br />
          <Form.Item>
            <Button block type="primary" htmlType="submit">
              {i18next.t("forget:Next Step")}
            </Button>
          </Form.Item>
        </Form>

        {/* STEP 2: verify email or phone */}
        <Form
          hidden={this.state.current !== 1}
          ref={this.form}
          name="step2"
          onFinishFailed={(errorInfo) =>
            this.onFinishFailed(
              errorInfo.values,
              errorInfo.errorFields,
              errorInfo.outOfDate
            )
          }
          initialValues={{
            application: application.uid,
            organization: application.organization,
          }}
          style={Setting.isMobile() ? {} : {width: "300px"}}
          size="large"
        >
          <Form.Item
            style={Setting.isMobile() ? {height: 0, visibility: "hidden", marginBottom: 12}
              : {height: 0, visibility: "hidden"}}
            name="application"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "forget:Please input your application!"
                ),
              },
            ]}
          />
          <Form.Item
            style={Setting.isMobile() ? {height: 0, visibility: "hidden", marginBottom: 12}
              : {height: 0, visibility: "hidden"}}
            name="organization"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "forget:Please input your organization!"
                ),
              },
            ]}
          />
          <Form.Item
            name="email" // use email instead of email/phone to adapt to RequestForm in account.go
            validateFirst
            hasFeedback
          >
            {
              this.state.isFixed ? <Input disabled defaultValue={this.state.fixedContent} /> :
                <Select
                  key={this.state.verifyType}
                  virtual={false} style={{textAlign: "left"}}
                  placeholder={i18next.t("forget:Choose email or phone")}
                  onChange={(value) => {
                    this.setState({
                      verifyType: value,
                    });
                    if (value === "phone") {
                      this.setState({
                        username: this.state.phone,
                      });
                    } else if (value === "email") {
                      this.setState({
                        username: this.state.email,
                      });
                    }
                  }}
                >
                  {
                    this.renderOptions()
                  }
                </Select>
            }
          </Form.Item>
          <Form.Item
            name="emailCode" // use emailCode instead of email/phoneCode to adapt to RequestForm in account.go
            rules={[
              {
                required: true,
                message: i18next.t(
                  "code:Please input your verification code!"
                ),
              },
            ]}
            style={Setting.isMobile() ? {marginBottom: 12} : {}}
          >
            {this.state.verifyType === "email" ? (
              <CountDownInput
                from="forget"
                disabled={this.state.username === "" || this.state.verifyType === ""}
                onButtonClickArgs={[this.state.email, "email", Setting.getApplicationName(this.state.application), this.state.name]}
                application={application}
              />
            ) : (
              <CountDownInput
                from="forget"
                disabled={this.state.username === "" || this.state.verifyType === ""}
                onButtonClickArgs={[this.state.phone, "phone", Setting.getApplicationName(this.state.application), this.state.name]}
                application={application}
              />
            )}
          </Form.Item>
          <br />
          <Form.Item>
            <Button
              block
              type="primary"
              htmlType="submit"
            >
              {i18next.t("forget:Next Step")}
            </Button>
          </Form.Item>
        </Form>

        {/* STEP 3 */}
        <Form
          hidden={this.state.current !== 2}
          ref={this.form}
          name="step3"
          onFinish={(values) => this.onFinish(values)}
          onFinishFailed={(errorInfo) =>
            this.onFinishFailed(
              errorInfo.values,
              errorInfo.errorFields,
              errorInfo.outOfDate
            )
          }
          initialValues={{
            application: application.uid,
            organization: application.organization,
          }}
          style={Setting.isMobile() ? {} : {width: "300px"}}
          size="large"
        >
          <Form.Item
            style={Setting.isMobile() ? {height: 0, visibility: "hidden", marginBottom: 12}
              : {height: 0, visibility: "hidden"}}
            name="application"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "forget:Please input your application!"
                ),
              },
            ]}
          />
          <Form.Item
            style={Setting.isMobile() ? {height: 0, visibility: "hidden", marginBottom: 12}
              : {height: 0, visibility: "hidden"}}
            name="organization"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "forget:Please input your organization!"
                ),
              },
            ]}
          />
          <Form.Item
            name="newPassword"
            hidden={this.state.current !== 2}
            rules={[
              {
                required: true,
                message: i18next.t(
                  "login:Please input your password!"
                ),
              },
            ]}
            hasFeedback
          >
            <Input.Password
              disabled={this.state.userId === ""}
              prefix={<LockOutlined />}
              placeholder={i18next.t("forget:Password")}
            />
          </Form.Item>
          <Form.Item
            name="confirm"
            dependencies={["newPassword"]}
            hasFeedback
            style={Setting.isMobile() ? {marginBottom: 12} : {marginBottom: 12}}
            rules={[
              {
                required: true,
                message: i18next.t(
                  "signup:Please confirm your password!"
                ),
              },
              ({getFieldValue}) => ({
                validator(rule, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    i18next.t(
                      "forget:Your confirmed password is inconsistent with the password!"
                    )
                  );
                },
              }),
            ]}
          >
            <Input.Password
              disabled={this.state.userId === ""}
              prefix={<CheckCircleOutlined />}
              placeholder={i18next.t("forget:Confirm")}
            />
          </Form.Item>
          <Row>
            <Col>
              <div style={{fontSize: Setting.isMobile() ? 12 : 14, color: "#999999", textAlign: "left", marginBottom: 12}}>
                {Setting.getPasswordRuleText(this.state.pwdPolicy)}
              </div>
            </Col>
          </Row>
          <br />
          <Form.Item hidden={this.state.current !== 2}>
            <Button block type="primary" htmlType="submit" disabled={this.state.userId === ""}>
              {i18next.t("forget:Change Password")}
            </Button>
          </Form.Item>
        </Form>
      </Form.Provider>
    );
  }

  render() {
    const application = this.getApplicationObj();
    if (application === null) {
      return Util.renderMessageLarge(this, Setting.getResponseMessage(this.state.msg), () => {
        const params = new URLSearchParams(window.location.search);
        const loginUrl = Util.decodeUrl(params.get("login_url"));
        if (loginUrl) {
          Setting.goToLink(loginUrl);
        } else {
          Setting.goToLink("/");
        }
      });
    }

    const antCol24Mobile = {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        background: `url(${
          require("../../assets/images/building.png").default
        }) center top no-repeat`,
        position: "relative",
      },
      antCol24DivMobile = {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        height: 420,
        width: "82.6%",
        boxSizing: "border-box",
        padding: "0 26px",
        backgroundColor: "rgba(255, 255, 255, 1)",
        borderRadius: 8,
        boxShadow: "4px 4px 24px 0px rgba(54, 85, 141, 0.1)",
        position: "relative",
      },
      styleDisplaynameMobile = {
        color: "#46494C",
        textAlign: "center",
        fontSize: 19,
        fontWeight: "bold",
        margin: 0,
      },
      styleLogoMobile = {
        position: "absolute",
        left: "calc(50% - 35px)",
        top: -35,
        // border: "6px solid #fff",
        // backgroundColor: "#fff",
        // borderRadius: 41,
      },
      styleToggleLanguageMobile = {
        position: "absolute",
        top: 20,
        right: 16,
        fontSize: 13,
        backgroundColor: "rgba(252, 252, 252, 0.7)",
        color: "#2078FF",
        padding: "6px 16px",
        borderRadius: 3,
      };
    const menuBtnMobile = <div style={styleToggleLanguageMobile}>{i18next.t("language:Selected language")}</div>;
    return (
      Setting.isMobile() ?
        <Row style={{height: "100vh", width: "100vw",
          background: "linear-gradient(to bottom, #349BFB, 40% , #ffffff)"}}>
          <Col span={24} style={antCol24Mobile}>
            <SelectLanguageBox menuBtn={menuBtnMobile}></SelectLanguageBox>
            <div style={antCol24DivMobile}>
              <img src={require("../../assets/images/logo_mobile.png").default} alt="" style={styleLogoMobile} />
              <div style={{width: "100%", marginTop: 63, marginBottom: 34}}>
                <p style={styleDisplaynameMobile}>{i18next.t("forget:Retrieve password")}</p>
              </div>
              <Row style={{height: 30}}>
                <Col span={24} style={{height: "100%"}}>
                  <Steps
                    current={this.state.current}
                    responsive={false}
                    style={{
                      width: "90%",
                      maxWidth: "500px",
                      margin: "auto",
                      height: "100%",
                    }}
                  >
                    <Step
                      title={i18next.t("forget:Account")}
                      icon={<UserOutlined />}
                    />
                    <Step
                      title={i18next.t("forget:Verify")}
                      icon={<SolutionOutlined />}
                    />
                    <Step
                      title={i18next.t("forget:Reset")}
                      icon={<KeyOutlined />}
                    />
                  </Steps>
                </Col>
              </Row>
              <div style={{marginTop: "10px", textAlign: "center"}}>
                {this.renderForm(application)}
              </div>
            </div>
          </Col>
        </Row>
        :
        <Row>
          <Col span={24} style={{justifyContent: "center"}}>
            <Row>
              <Col span={24}>
                <div style={{marginTop: "80px", marginBottom: "10px", textAlign: "center"}}>
                  {
                    Setting.renderHelmet(application)
                  }
                  <CustomGithubCorner />
                </div>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <div style={{textAlign: "center", fontSize: "28px"}}>
                  {i18next.t("forget:Retrieve password")}
                </div>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Steps
                  current={this.state.current}
                  style={{
                    width: "90%",
                    maxWidth: "500px",
                    margin: "auto",
                    marginTop: "80px",
                  }}
                >
                  <Step
                    title={i18next.t("forget:Account")}
                    icon={<UserOutlined />}
                  />
                  <Step
                    title={i18next.t("forget:Verify")}
                    icon={<SolutionOutlined />}
                  />
                  <Step
                    title={i18next.t("forget:Reset")}
                    icon={<KeyOutlined />}
                  />
                </Steps>
              </Col>
            </Row>
          </Col>
          <Col span={24} style={{display: "flex", justifyContent: "center"}}>
            <div style={{marginTop: "10px", textAlign: "center"}}>
              {this.renderForm(application)}
            </div>
          </Col>
        </Row>
    );
  }
}

export default ForgetPage;
